import { API_BASE_URL } from '@/utils/constants';
import HttpClient from '@/utils/http-client';
import { useState } from 'react';

export type DateRange = {
  start_date: string;
  end_date: string;
};

export type AuditScoreRange = {
  min: number;
  max: number;
};

export type QualificationQuestion = {
  capture_id: string;
  capture_output: string[] | number[] | boolean | { min: number; max: number };
};

export type ConversationFilters = {
  search_date?: DateRange;
  customer_id?: number;
  conversation_owner_ids?: number[];
  crm_status_ids?: number[];
  concern_categories?: string[];
  agent_pitch_gaps?: string[];
  intents?: string[];
  qualification_questions?: QualificationQuestion[];
  audit_score_range?: AuditScoreRange;
};

export type CallOverview = {
  total_calls: number;
  contact_count: number;
  detailed_calls: number;
  avg_call_duration: number;
};

export type BuyingIntentItem =
  | { intent: 'HIGH_INTENT'; count: number }
  | { intent: 'MODERATE'; count: number }
  | { intent: 'LOW_INTENT'; count: number }
  | { intent: 'NEUTRAL'; count: number }
  | { intent: 'NOT_AVAILABLE'; count: number };

export type LeadConcern = {
  category: string;
  count: number;
};

export type KeyInsightsResponse = {
  key_insights: any[];
  lead_concerns: LeadConcern[];
  agent_pitch_gaps: any[];
};

export type CustomerListItem = {
  crm_status: string;
  customer_name: string;
  email: string;
  phone: string;
  total_conversations: number;
  last_conversation: string;
  intent: BuyingIntentItem['intent'];
  quality_score: number;
  assignees: { name: string; avatar_url: string }[];
  customer_id?: any;
};

export function useConversationsInsights(
  workspaceId: number,
  page: number,
  limit: number,
  filters: any,
  sortField?: string,
  sortOrder?: 'asc' | 'desc',
) {
  // Objection Categories
  const [objections, setObjections] = useState<string[]>([]);
  const [loadingObjections, setLoadingObjections] = useState(false);

  async function fetchObjections() {
    setLoadingObjections(true);
    try {
      const { data } = await HttpClient.get(
        `${API_BASE_URL}/v1/customer/insights/objection-categories/${workspaceId}`,
      );

      // console.log('fetchObjections', data.data);

      setObjections(data.data);
      return data.data;
    } catch (error) {
      console.log("Error", error);
      setObjections([]);
      return [];
    } finally {
      setLoadingObjections(false);
    }
  }

  // Call Overview
  const [overview, setOverview] = useState<CallOverview | null>(null);
  const [loadingOverview, setLoadingOverview] = useState(false);

  async function fetchOverview(payload?: any) {
    setLoadingOverview(true);
    try {
      const { data } = await HttpClient.post(
        `${API_BASE_URL}/v1/customer/insights/call-overview/${workspaceId}`,
        { filters: payload || filters },
      );
      setOverview(data?.data);
      return data.data;
    } catch (error) {
      console.log("Error", error);
      setOverview(null);
      return null;
    } finally {
      setLoadingOverview(false);
    }
  }

  // Buying Intent Spread
  const [intentSpread, setIntentSpread] = useState<BuyingIntentItem[]>([]);
  const [loadingIntentSpread, setLoadingIntentSpread] = useState(false);

  async function fetchIntentSpread(payload?: any) {
    setLoadingIntentSpread(true);
    try {
      const { data } = await HttpClient.post(
        `${API_BASE_URL}/v1/customer/insights/buying-intent/spread/${workspaceId}`,
        { filters: payload || filters },
      );
      setIntentSpread(data.data);
      return data.data;
    } catch (error) {
      console.log("Error", error);
      setIntentSpread([]);
      return [];
    } finally {
      setLoadingIntentSpread(false);
    }
  }

  // Key Insights
  const [keyInsights, setKeyInsights] = useState<KeyInsightsResponse | null>(
    null,
  );
  const [loadingKeyInsights, setLoadingKeyInsights] = useState(false);

  async function fetchKeyInsights(payload?: any) {
    setLoadingKeyInsights(true);
    try {
      const { data } = await HttpClient.post(
        `${API_BASE_URL}/v1/customer/insights/key-insights/${workspaceId}`,
        { filters: payload || filters },
      );
      setKeyInsights(data.data);
      return data.data;
    } catch (error) {
      console.log("Error", error);
      setKeyInsights(null);
      return null;
    } finally {
      setLoadingKeyInsights(false);
    }
  }

  // Customer List
  const [customers, setCustomers] = useState<CustomerListItem[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  async function fetchCustomers(payload?: any, nextPage?: any) {
    setLoadingCustomers(true);
    try {
      // const qs = new URLSearchParams({
      //   sort_field: sortField,
      //   sort_order: sortOrder,
      // }).toString()
      // const suffix = qs ? `?${qs}` : ''

      const { data } = await HttpClient.post(
        // `${API_BASE_URL}/v1/customer/insights/customer-list/${workspaceId}${suffix}`,
        `${API_BASE_URL}/v1/customer/insights/customer-list/${workspaceId}`,
        { page: nextPage || page, limit, filters: payload || filters },
      );
      const pageData = data.data.data;
      setCustomers(pageData);
      return pageData;
    } catch (error) {
      console.log("Error", error);
      setCustomers([]);
      return [];
    } finally {
      setLoadingCustomers(false);
    }
  }

  const [agentPitchGaps, setAgentPitchGaps] = useState<
    { id: string; label: string }[]
  >([]);
  const [loadingAgentPitchGaps, setLoadingAgentPitchGaps] = useState(false);

  async function fetchAgentPitchGaps() {
    setLoadingAgentPitchGaps(true);
    try {
      const { data } = await HttpClient.get(
        `${API_BASE_URL}/v1/customer/insights/agent-pitch-gaps/${workspaceId}`,
      );

      setAgentPitchGaps(data.data);
      return data.data;
    } catch (error) {
      console.log("Error", error);
      setAgentPitchGaps([]);
      return [];
    } finally {
      setLoadingAgentPitchGaps(false);
    }
  }

  return {
    // data
    objections,
    overview,
    intentSpread,
    keyInsights,
    customers,
    agentPitchGaps,

    // loading flags
    loadingObjections,
    loadingOverview,
    loadingIntentSpread,
    loadingKeyInsights,
    loadingCustomers,
    loadingAgentPitchGaps,

    // fetch functions
    fetchObjections,
    fetchOverview,
    fetchIntentSpread,
    fetchKeyInsights,
    fetchCustomers,
    fetchAgentPitchGaps,
  };
}
